/**
 * Confirmación con motivo obligatorio (cancelar una campaña, reanudar la marca, ajustar saldo).
 *
 * Como `ConfirmDangerModal` (foco atrapado con `useFocusTrap`), más un `<textarea>` de 1 a 500
 * caracteres: el motivo queda en la auditoría, y una auditoría con «ok» o vacío no le sirve a
 * nadie cuando hay que reconstruir por qué se frenó un envío.
 */

import { useEffect, useId, useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { useFocusTrap } from "../../../hooks/useFocusTrap";

export const MAX_MOTIVO = 500;

interface Props {
  titulo: string;
  descripcion: string;
  /** texto del botón de confirmar */
  confirmar: string;
  peligro?: boolean;
  onConfirmar: (motivo: string) => void;
  onCancelar: () => void;
  busy: boolean;
  /** el motivo ya cargado (se puede cambiar): por ejemplo, la prueba del Sandbox que superó el freno */
  motivoInicial?: string;
}

export function ModalMotivo({ titulo, descripcion, confirmar, peligro = false, onConfirmar, onCancelar, busy, motivoInicial = "" }: Props) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [motivo, setMotivo] = useState(() => motivoInicial.slice(0, MAX_MOTIVO));
  const idTitulo = useId();
  const idMotivo = useId();
  const limpio = motivo.trim();
  const valido = limpio.length >= 1 && limpio.length <= MAX_MOTIVO;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onCancelar(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onCancelar]);

  const enviar = () => { if (valido && !busy) onConfirmar(limpio); };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4">
      <div ref={trapRef} role="dialog" aria-modal="true" aria-labelledby={idTitulo} className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 id={idTitulo} className="font-semibold text-lg text-slate-900">{titulo}</h3>
          <button onClick={onCancelar} disabled={busy} aria-label="Cerrar"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all disabled:opacity-50">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${peligro ? "bg-red-100" : "bg-amber-50"}`}>
              <AlertTriangle size={22} className={peligro ? "text-red-600" : "text-amber-600"} />
            </div>
            <p className="text-slate-600 text-sm leading-relaxed pt-1">{descripcion}</p>
          </div>
          <div>
            <label htmlFor={idMotivo} className="text-xs font-semibold text-slate-600">Motivo (queda registrado)</label>
            <textarea
              id={idMotivo}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value.slice(0, MAX_MOTIVO))}
              rows={3}
              maxLength={MAX_MOTIVO}
              disabled={busy}
              className="w-full mt-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-slate-50"
            />
            <p className="text-[11px] text-slate-400 mt-1 text-right">{motivo.length} / {MAX_MOTIVO}</p>
          </div>
        </div>

        <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button onClick={onCancelar} disabled={busy}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            Volver
          </button>
          <button onClick={enviar} disabled={!valido || busy}
            className={`flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 text-white disabled:opacity-50 disabled:cursor-not-allowed ${
              peligro ? "bg-red-600 hover:bg-red-700" : "bg-violet-600 hover:bg-violet-700"
            }`}>
            {busy && <Loader2 size={14} className="animate-spin" />}
            {confirmar}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ModalMotivo;
